import React from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { IClinic } from '@app/interfaces/clinic.interface';
import { saveTemplate } from '@app/api/clinic';
import { MESSAGE_FIELD_NAME } from '@app/constants/clinic';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';

interface Props {
  clinic: IClinic;
  setClinic: (clinic: IClinic) => void;
}

const DEFAULT_MESSAGE = 'Будем признательны, если Вы уделите минутку и оставите о нас отзыв.';

function SuccessFeedbackMessageEditor({ clinic, setClinic }: Props) {
  const [template, setTemplate] = React.useState(clinic.successFeedbackMessageTemplate || '');

  const templateConfig = React.useMemo(
    () => [
      {
        tmpl: '%FIRST_NAME%',
        label: 'Имя пациента',
        value: 'Иван',
      },
      {
        tmpl: '%SMS_PRICE%',
        label: 'Стоимость sms',
        value: clinic.smsPrice,
      },
    ],
    [clinic.smsPrice]
  );

  const message = React.useMemo(() => {
    let tmplString = template;

    templateConfig.forEach(({ tmpl, value }) => {
      tmplString = tmplString.replace(tmpl, value);
    });

    return tmplString;
  }, [templateConfig, template]);

  const handleTemplateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplate(e.target.value);
  };

  const handleTemplateSave = async () => {
    saveTemplate(clinic.id, MESSAGE_FIELD_NAME.SUCCESS_FEEDBACK, template).then((clinic) => {
      if (clinic) {
        setClinic(clinic);
      }
    });
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
        <Typography variant='h5' color='forestgreen'>
          Позитивный отзыв
        </Typography>

        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton onClick={() => setTemplate('')} disabled={!template}>
            <HighlightOffIcon color={template ? 'error' : 'disabled'} />
          </IconButton>
          <IconButton onClick={() => setTemplate(DEFAULT_MESSAGE)}>
            <RotateLeftIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Paper variant='outlined' sx={{ padding: 2 }}>
        {templateConfig.map(({ tmpl, label }) => (
          <Typography key={label}>{`${tmpl} - ${label}`}</Typography>
        ))}
      </Paper>

      <Divider />

      <GridWrapper>
        <Editor
          id='welcome-feedback-message-template'
          label='Шаблон'
          multiline
          rows={5}
          fullWidth
          value={template}
          onChange={handleTemplateChange}
        />

        <Editor
          id='welcome-feedback-message-example'
          label='Итог'
          multiline
          rows={5}
          fullWidth
          disabled
          value={message}
        />
      </GridWrapper>

      <Divider />

      <Button
        onClick={handleTemplateSave}
        disabled={clinic?.successFeedbackMessageTemplate === template}
      >
        Сохранить
      </Button>
    </Box>
  );
}

const Editor = styled(TextField)<TextFieldProps>({
  '.MuiInputBase-root': {
    background: '#f8f9ff',
  },
});

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Divider = styled.div`
  height: 30px;
`;

export default SuccessFeedbackMessageEditor;
